import { useEffect } from 'react';
import dayjs from 'dayjs';
import { App, Flex, Form, Input, Space, Typography } from 'antd';
import { InputDate } from '@/components/Inputs/InputDate';
import { SelectInsuranceCarrier } from '@/features/insuranceCarriers/components/SelectInsuranceCarrier';
import { SectionHeader } from './SectionHeader';
import { useCreateAgreement, useGetAgreement, useUpdateAgreement } from '@/features/agreements/hooks/useAgreements';
import { getErrorMessage } from '@/shared/utils/errors/getErrorMessage';

const { Text } = Typography;

export const BasicInfoStep = ({ agreementId, setAgreementId, onNextRef, readOnly = false }) => {
  const [form] = Form.useForm();
  const { notification } = App.useApp();
  const createAgreementMutation = useCreateAgreement();
  const updateAgreementMutation = useUpdateAgreement();
  const agreementQuery = useGetAgreement(agreementId, { enabled: !!agreementId });

  /** Sincroniza el formulario con los datos de un convenio */
  const syncFormWithAgreement = (agreement) => {
    if (!agreement) return;
    form.setFieldsValue({
      'name': agreement.name,
      'insuranceCarrier': agreement.insuranceCarrier?.id,
      'startDate': agreement.startDate,
      'endDate': agreement.endDate,
    });
  };

  useEffect(() => {
    if (agreementQuery.data) {
      syncFormWithAgreement(agreementQuery.data);
    }
  }, [agreementQuery.data]);

  const handleNext = async () => {
    if (readOnly) return;

    const values = await form.validateFields();

    if (agreementId) {
      await updateAgreementMutation.mutateAsync({ id: agreementId, data: values }, {
        onSuccess: (updatedAgreement) => {
          syncFormWithAgreement(updatedAgreement);

          notification.success({
            message: <Text strong>Convenio actualizado</Text>,
            description: '¡El convenio se ha actualizado correctamente!',
          });
        },
        onError: (error) => {
          notification.error({
            message: <Text strong>Convenio no actualizado</Text>,
            description: getErrorMessage(error),
          });
        },
      });

      return;
    }

    await createAgreementMutation.mutateAsync(values, {
      onSuccess: (newAgreement) => {
        setAgreementId?.(newAgreement.id);
        syncFormWithAgreement(newAgreement);

        notification.success({
          message: <Text strong>Convenio creado</Text>,
          description: '¡El convenio se ha creado correctamente!',
        });
      },
      onError: (error) => {
        notification.error({
          message: <Text strong>Convenio no creado</Text>,
          description: getErrorMessage(error),
        });
      },
    });
  };

  useEffect(() => {
    if (!onNextRef) return;
    onNextRef.current = handleNext;

    return () => {
      onNextRef.current = null;
    };
  }, [handleNext, onNextRef]);

  return (
    <Flex justify='center' align='center'>
      <div style={{ width: '100%', maxWidth: 470 }}>
        <Form
          layout='vertical'
          form={form}
          disabled={readOnly || createAgreementMutation.isPending}
        >
          <Flex vertical gap='large'>
            <Flex vertical gap='middle'>
              <SectionHeader
                title='¿Qué convenio estás creando?'
                description='Define un nombre descriptivo del convenio y selecciona la aseguradora con la que se establecerá el convenio.'
              />
              <Form.Item
                label='Nombre del convenio'
                name='name'
                rules={[{ required: true }]}
                style={{ marginBottom: 0 }}
              >
                <Input placeholder='Nombre del convenio' />
              </Form.Item>
              <Form.Item
                label='Aseguradora'
                name='insuranceCarrier'
                rules={[{ required: true }]}
              >
                <SelectInsuranceCarrier />
              </Form.Item>
            </Flex>
            <Flex vertical gap='middle'>
              <SectionHeader
                title='¿Cuándo debe aplicarse?'
                description='Define las fechas de inicio y fin de la vigencia del convenio.'
              />
              <Form.Item
                label='Fecha inicio'
                name='startDate'
                rules={[{ required: true }]}
                style={{ marginBottom: 0 }}
              >
                <InputDate allowClear={false} />
              </Form.Item>
              <Form.Item
                label='Fecha fin'
                name='endDate'
                dependencies={['startDate']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, endDate) {
                      const startDate = getFieldValue('startDate');

                      if (!endDate || !startDate) {
                        return Promise.resolve();
                      }

                      const startDateDayjs = dayjs(startDate);
                      const endDateDayjs = dayjs(endDate);

                      if (endDateDayjs.isSame(startDateDayjs, 'day') || endDateDayjs.isAfter(startDateDayjs, 'day')) { // 'day' ignora la hora en la comparación y valida solo la fecha
                        return Promise.resolve();
                      }

                      return Promise.reject(
                        new Error('Debe ser mayor o igual a fecha inicio'),
                      );
                    },
                  }),
                ]}
              >
                <InputDate />
              </Form.Item>
            </Flex>
          </Flex>
        </Form>
      </div>
    </Flex>
  );
};